import React, { Component } from 'react';

import appSettings from '../../config/settings';
import * as log from '../../util/log';

import { LoadableComponentErrorBoundaryPage } from './LoadableComponentErrorBoundaryPage';

// Webpack gives this name to both JS and CSS code chunks that fail to load.
const isChunkLoadError = error => error?.name === 'ChunkLoadError';

// P25028-129: a failed chunk usually means a new version was deployed while the page was open.
// A full reload fetches the fresh code, so we try that once before showing any error.
// The note below remembers that reload, so that a chunk that is really missing
// does not put the page into a reload loop.
const RELOAD_STORAGE_KEY = 'LoadableComponentErrorBoundary.reload';
const RELOAD_GUARD_MS = 60 * 1000;

const hasReloadedRecently = path => {
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(RELOAD_STORAGE_KEY));
    return saved?.path === path && Date.now() - saved.timestamp < RELOAD_GUARD_MS;
  } catch (e) {
    return false;
  }
};

/**
 * Reloads the current page, unless it was already reloaded because of a ChunkLoadError.
 *
 * @returns {boolean} true if the page is reloading
 */
const reloadOnce = () => {
  const path = window.location.pathname;
  if (hasReloadedRecently(path)) {
    return false;
  }
  try {
    window.sessionStorage.setItem(
      RELOAD_STORAGE_KEY,
      JSON.stringify({ path, timestamp: Date.now() })
    );
  } catch (e) {
    // Without storage we can't prevent a reload loop, so we show the error page instead.
    return false;
  }
  window.location.reload();
  return true;
};

// Use ErrorBoyndary to catch ChunkLoadError
// https://reactjs.org/docs/error-boundaries.html
export class LoadableComponentErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, showErrorPage: false };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { error };
  }

  componentDidCatch(error) {
    if (!isChunkLoadError(error)) {
      return;
    }
    const reloading = reloadOnce();
    log.error(error, 'chunk-load-failed', { path: window.location.pathname, reloading });
    if (!reloading) {
      this.setState({ showErrorPage: true });
    }
  }

  render() {
    const { error, showErrorPage } = this.state;
    if (isChunkLoadError(error)) {
      const { routeName, params, location } = this.props;
      // Checkout sends the buyer to the new order with this flag (see CheckoutPageWithPayment).
      const isNewOrder = routeName === 'OrderDetailsPage' && !!location?.state?.fromCheckout;

      // Keep the screen empty while the page reloads itself.
      return showErrorPage ? (
        <LoadableComponentErrorBoundaryPage orderId={isNewOrder ? params?.id : null} />
      ) : null;
    }

    return this.props.children;
  }
}

// LoadableComponentErrorBoundary helps in situations
// where production build changes in the server and
// long-living client app tries to fetch code chunks that don't exist anymore.
// Note: in development mode with Hot Module Reloading (HMR) in use, this causes error loops.
const UseLoadableErrorBoundaryOnlyInProdutionMode = props => {
  const { children, ...rest } = props;
  return appSettings.dev ? (
    children
  ) : (
    <LoadableComponentErrorBoundary {...rest}>{children}</LoadableComponentErrorBoundary>
  );
};
export default UseLoadableErrorBoundaryOnlyInProdutionMode;
